import React, { useMemo } from 'react'
import { Wizard, WizardStep } from '@e3dc-react/shell/Elements/Wizard'
import { Address } from '../Address'
import { Storage } from '../Storage'
import { Overview } from '../Overview'
import { Photovoltaic } from '../Photovoltaic'
import { calculateButtonType } from '@e3dc-react/shell/Elements/Wizard/wizard-items/utils'
import { InstallationStatementStep } from 'src/Libs/installation-statement/types/general'
import { AddressFormData, OverviewStepFormData } from 'src/Libs/installation-statement/types'
import { StorageInstallationStatementModel } from 'src/Libs/installation-statement/models'
import { PhotovoltaicFormData } from 'src/Libs/installation-statement/configurations/photovoltaic'
import { Images, Measurements } from 'src/Libs/installation-statement/components'
import { ImagesFormData } from 'src/Libs/installation-statement/models/images'
import { MeasurementsFormData } from 'src/Libs/installation-statement/models/measurements'
import { EdisonWallBoxFormData } from 'src/Libs/installation-statement/models/edisonWallBox'
import { EdisonWallBox } from 'src/Libs/installation-statement/components/EdisonWallbox'
import { StorageInstallationStatementKeys } from 'src/Libs/installation-statement/models/storage'
import { I1ElectricalChargingStation } from '@e3dc-react/icons'
import { makeStyles } from 'tss-react/mui'
import { useIsWebviewMode } from 'src/Hooks'

interface InstallationStatementProps {
  addressStep: InstallationStatementStep<AddressFormData>
  storageStep: InstallationStatementStep<StorageInstallationStatementModel>
  overviewStep: InstallationStatementStep<OverviewStepFormData>
  photovoltaicStep: InstallationStatementStep<PhotovoltaicFormData>
  imagesStep: InstallationStatementStep<ImagesFormData>
  measurementsStep: InstallationStatementStep<MeasurementsFormData>
  edisonStep: InstallationStatementStep<EdisonWallBoxFormData>
  onConfirm: () => void
}
/**
 * This component provides an installation statement wizard for an installation
 * The process of the statement is pictured in our [docs](https://docs.e3dc.com/x/5Q6hB)
 *
 * @param addressStep - data for the address tab
 * @param storageStep - data for the storage tab
 * @param photovoltaicStep - data for the photovoltaic tab
 * @param overviewStep - data for the overview tab
 * @param imagesStep - data for the images tab
 * @param measurementsStep - data for the measurements tab
 * @param onConfirm - function to call when we want to save the completed statement
 */

const useStyles = makeStyles()(theme => ({
  wizard: {
    [theme.breakpoints.down('sm')]: {
      bottom: '60px',
    },
  },
}))

const InstallationStatement: React.FC<InstallationStatementProps> = ({
  addressStep,
  storageStep,
  overviewStep,
  photovoltaicStep,
  imagesStep,
  measurementsStep,
  edisonStep,
  onConfirm,
}) => {
  const hasMissingRequiredFields = useMemo(() => {
    return [
      addressStep.isFinalValidationValid,
      storageStep.isFinalValidationValid,
      photovoltaicStep.isFinalValidationValid,
      measurementsStep.isFinalValidationValid,
      imagesStep.isFinalValidationValid,
      storageStep.formData.values.showEdisonWallboxStep ? edisonStep.isFinalValidationValid : true,
    ].some(finalValidation => {
      return finalValidation === false
    })
  }, [
    addressStep.isFinalValidationValid,
    storageStep.isFinalValidationValid,
    photovoltaicStep.isFinalValidationValid,
    measurementsStep.isFinalValidationValid,
    imagesStep.isFinalValidationValid,
    edisonStep.isFinalValidationValid,
    storageStep.formData.values.showEdisonWallboxStep,
  ])

  const isWebView = useIsWebviewMode()
  const { classes } = useStyles()

  const isLastStepValid = useMemo(() => {
    return overviewStep.formData.values.accepted && !hasMissingRequiredFields
  }, [overviewStep, hasMissingRequiredFields])

  const address = useMemo(
    () => (
      <Address
        data={addressStep.formData.values}
        setFormData={addressStep.setFormData}
        setFinalValidation={addressStep.setFinalValidation}
        setTouched={addressStep.setTouched}
      />
    ),
    [addressStep.formData.values, addressStep.setFinalValidation, addressStep.setFormData, addressStep.setTouched]
  )

  const storage = useMemo(
    () => (
      <Storage
        data={storageStep.formData.values}
        setFormData={storageStep.setFormData}
        setFinalValidation={storageStep.setFinalValidation}
        setTouched={storageStep.setTouched}
      />
    ),
    [storageStep.formData.values, storageStep.setFinalValidation, storageStep.setFormData, storageStep.setTouched]
  )

  const images = useMemo(
    () => (
      <Images
        data={imagesStep.formData.values}
        setFormData={imagesStep.setFormData}
        setFinalValidation={imagesStep.setFinalValidation}
        setTouched={imagesStep.setTouched}
      />
    ),
    [imagesStep.formData.values, imagesStep.setFormData, imagesStep.setFinalValidation, imagesStep.setTouched]
  )

  const overview = useMemo(
    () => (
      <Overview
        data={{
          address: addressStep.formData.values,
          storage: storageStep.formData.values,
          overview: overviewStep.formData.values,
          photovoltaic: photovoltaicStep.formData.values,
          measurements: measurementsStep.formData.values,
          images: imagesStep.formData.values,
          edison: edisonStep.formData.values,
        }}
        setFormData={overviewStep.setFormData}
      />
    ),
    [
      addressStep.formData.values,
      storageStep.formData.values,
      overviewStep.formData.values,
      photovoltaicStep.formData.values,
      measurementsStep.formData.values,
      imagesStep.formData.values,
      edisonStep.formData.values,
      overviewStep.setFormData,
    ]
  )

  const photovoltaic = useMemo(
    () => (
      <Photovoltaic
        data={photovoltaicStep.formData.values}
        setFormData={photovoltaicStep.setFormData}
        setFinalValidation={photovoltaicStep.setFinalValidation}
        setTouched={photovoltaicStep.setTouched}
      />
    ),
    [photovoltaicStep.formData.values, photovoltaicStep.setFormData, photovoltaicStep.setFinalValidation, photovoltaicStep.setTouched]
  )

  const edison = useMemo(
    () => (
      <EdisonWallBox
        data={edisonStep.formData.values}
        setFormData={edisonStep.setFormData}
        setFinalValidation={edisonStep.setFinalValidation}
        setTouched={edisonStep.setTouched}
      />
    ),
    [edisonStep.formData.values, edisonStep.setFormData, edisonStep.setFinalValidation, edisonStep.setTouched]
  )

  const measurements = useMemo(
    () => (
      <Measurements
        data={measurementsStep.formData.values}
        setFormData={measurementsStep.setFormData}
        setFinalValidation={measurementsStep.setFinalValidation}
        setTouched={measurementsStep.setTouched}
      />
    ),
    [measurementsStep.formData.values, measurementsStep.setFormData, measurementsStep.setFinalValidation, measurementsStep.setTouched]
  )

  const wizardSteps: WizardStep[] = [
    {
      content: address,
      buttonType: calculateButtonType({
        isInProgressValidationValid: Boolean(addressStep.formData.isValid) || !addressStep.formData.dirty,
        isFinalValidationValid: addressStep.isFinalValidationValid,
        isTouched: addressStep.isTouched,
        stepIsPrefilled: addressStep.stepIsPrefilled,
      }),
      label: addressStep.label,
      validationTooltipTitle: addressStep.validationTooltipTitle,
      stepNumber: 1,
    },
    {
      content: storage,
      label: storageStep.label,
      buttonType: calculateButtonType({
        isInProgressValidationValid: Boolean(storageStep.formData.isValid) || !storageStep.formData.dirty,
        isFinalValidationValid: storageStep.isFinalValidationValid,
        isTouched: storageStep.isTouched,
        stepIsPrefilled: storageStep.stepIsPrefilled,
      }),
      validationTooltipTitle: storageStep.validationTooltipTitle,
      stepNumber: 2,
    },
    {
      content: edison,
      label: edisonStep.label,
      buttonType: calculateButtonType({
        isInProgressValidationValid: Boolean(edisonStep.formData.isValid) || !edisonStep.formData.dirty,
        isFinalValidationValid: edisonStep.isFinalValidationValid,
        isTouched: edisonStep.isTouched,
        stepIsPrefilled: edisonStep.stepIsPrefilled,
      }),
      buttonIcon: I1ElectricalChargingStation,
      validationTooltipTitle: edisonStep.validationTooltipTitle,
      hidden: !storageStep.formData.values[StorageInstallationStatementKeys.showEdisonWallboxStep],
    },
    {
      content: photovoltaic,
      label: photovoltaicStep.label,
      buttonType: calculateButtonType({
        isInProgressValidationValid: Boolean(photovoltaicStep.formData.isValid) || !photovoltaicStep.formData.dirty,
        isFinalValidationValid: photovoltaicStep.isFinalValidationValid,
        isTouched: photovoltaicStep.isTouched,
        stepIsPrefilled: photovoltaicStep.stepIsPrefilled,
      }),
      validationTooltipTitle: photovoltaicStep.validationTooltipTitle,
      stepNumber: 3,
    },
    {
      content: measurements,
      label: measurementsStep.label,
      validationTooltipTitle: measurementsStep.validationTooltipTitle,
      buttonType: calculateButtonType({
        isInProgressValidationValid: Boolean(measurementsStep.formData.isValid) || !measurementsStep.formData.dirty,
        isFinalValidationValid: measurementsStep.isFinalValidationValid,
        isTouched: measurementsStep.isTouched,
        stepIsPrefilled: measurementsStep.stepIsPrefilled,
      }),
      stepNumber: 4,
    },
    {
      content: images,
      label: imagesStep.label,
      buttonType: calculateButtonType({
        isInProgressValidationValid: Boolean(imagesStep.formData.isValid) || !imagesStep.formData.dirty,
        isFinalValidationValid: imagesStep.isFinalValidationValid,
        isTouched: imagesStep.isTouched,
        stepIsPrefilled: imagesStep.stepIsPrefilled,
      }),
      validationTooltipTitle: imagesStep.validationTooltipTitle,
      stepNumber: 5,
    },
    {
      content: overview,
      label: overviewStep.label,
      validationTooltipTitle: overviewStep.validationTooltipTitle,
      buttonType: calculateButtonType({
        isInProgressValidationValid: overviewStep.formData.values.accepted,
        isFinalValidationValid: overviewStep.formData.values.accepted,
        isTouched: overviewStep.isTouched,
        stepIsPrefilled: overviewStep.stepIsPrefilled,
      }),
      stepNumber: 6,
    },
  ]

  return <Wizard steps={wizardSteps} isLastStepValid={isLastStepValid} onConfirm={onConfirm} className={isWebView ? classes.wizard : undefined} />
}

export default InstallationStatement
